/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/**
 * menu-aim is a jQuery plugin for dropdown menus that can differentiate
 * between a user trying hover over a dropdown item vs trying to navigate into
 * a submenu's contents.
 *
 * menu-aim assumes that you have are using a menu with submenus that expand
 * to the menu's right. It will fire events when the user's mouse enters a new
 * dropdown item *and* when that item is being intentionally hovered over.
 *
 * __________________________
 * | Monkeys  >|   Gorilla  |
 * | Gorillas >|   Content  |
 * | Chimps   >|   Here     |
 * |___________|____________|
 *
 * In the above example, "Gorillas" is selected and its submenu content is
 * being shown on the right. Imagine that the user's cursor is hovering over
 * "Gorillas." When they move their mouse into the "Gorilla Content" area, they
 * may briefly hover over "Chimps." This shouldn't close the "Gorilla Content"
 * area.
 *
 * This problem is normally solved using timeouts and delays. menu-aim tries to
 * solve this by detecting the direction of the user's mouse movement. This can
 * make for quicker transitions when navigating up and down the menu. The
 * experience is hopefully similar to amazon.com/'s "Shop by Department"
 * dropdown.
 *
 * Use like so:
 *
 *      menuAim(element, {
 *          activate: $.noop,  // fired on row activation
 *          deactivate: $.noop  // fired on row deactivation
 *      });
 *
 *  ...to receive events when a menu's row has been purposefully (de)activated.
 *
 * The following options can be passed to menuAim. All functions execute with
 * the relevant row's HTML element as the execution context ('this'):
 *
 *      menuAim(element, {
 *          // Function to call when a row is purposefully activated. Use this
 *          // to show a submenu's content for the activated row.
 *          activate: function() {},
 *
 *          // Function to call when a row is deactivated.
 *          deactivate: function() {},
 *
 *          // Function to call when mouse enters a menu row. Entering a row
 *          // does not mean the row has been activated, as the user may be
 *          // mousing over to a submenu.
 *          enter: function() {},
 *
 *          // Function to call when mouse exits a menu row.
 *          exit: function() {},
 *
 *          // Selector for identifying which elements in the menu are rows
 *          // that can trigger the above events. Defaults to "> li".
 *          rowSelector: "> li",
 *
 *          // You may have some menu rows that aren't submenus and therefore
 *          // shouldn't ever need to "activate." If so, filter submenu rows w/
 *          // this selector. Defaults to "*" (all elements).
 *          submenuSelector: "*",
 *
 *          // Direction the submenu opens relative to the main menu. Can be
 *          // left, right, above, or below. Defaults to "right".
 *          submenuDirection: "right"
 *      });
 *
 * https://github.com/kamens/jQuery-menu-aim
 *
 * Customized by Tamer Zorba in Xngage
 * adding event trigger to reset the menu, perform
 * fully minipulate to be native javascript and works with react
 * all events now are automatically injected with hookup
 * also when using react you need to unhook events on unmount
 */

type Coordinates = {
    x: number;
    y: number;
};

type EventHandler = (data: any, target?: any) => any;

type Options = {
    /** Function to call when a row is purposefully activated.
     *  Use this to show a submenu's content for the activated row. */

    activate?: EventHandler;
    /** Function to call when a row is deactivated */
    deactivate?: EventHandler;
    /** Function to call when mouse enters a menu row.
     *  Entering a row does not mean the row has been
     *  activated, as the user may be mousing over to a submenu. */
    enter?: EventHandler;
    /** Function to call when mouse exits a menu row. */
    exit?: EventHandler;
    /** Function to call when a menu row is clicked */
    click?: EventHandler;

    /** Function to call when mouse exits the entire menu. If this
     *  returns true, the current row's deactivation event and
     *  callback function will be fired. Otherwise, if this isn't
     *  supplied or it returns false, the currently activated row
     *  will stay activated when the mouse leaves the menu entirely. */
    exitMenu?: EventHandler;

    /** Selector for identifying which elements in the menu are rows
     *  that can trigger the above events. Defaults to "> li". */
    rowSelector?: string;
    /** You may have some menu rows that aren't submenus and therefore
     *  shouldn't ever need to "activate." If so, filter submenu rows
     *  w/ this selector.
     *  Defaults to "*" (all elements).
     */
    submenuSelector?: string;

    /** Direction the submenu opens relative to the main menu.
     *  This controls which direction is "forgiving" as the user
     *  moves their cursor from the main menu into the submenu.
     *  Can be one of "right", "left", "above", or "below".
     *  Defaults to "right". */
    submenuDirection?: "left" | "right" | "below" | "above";

    /** bigger = more forgiving when entering submenu */
    tolerance?: number;
};

function menuAim(menu: HTMLElement, opts: Options) {
    const noop = () => {};
    let activeRow: HTMLElement | null = null;
    const mouseLocs: Coordinates[] = [];
    let lastDelayLoc: Coordinates | null = null;
    let timeoutId: number | null = null;
    const options = {
        rowSelector: "> li",
        submenuSelector: "*",
        submenuDirection: "right",
        tolerance: 75, // bigger = more forgivey when entering submenu
        click: noop,
        enter: noop,
        exit: noop,
        activate: noop,
        deactivate: noop,
        exitMenu: noop,
        ...opts,
    };

    const MOUSE_LOCS_TRACKED = 3; // number of past mouse locations to track
    const DELAY = 100; // ms delay when user appears to be entering submenu

    /**
     * Keep track of the last few locations of the mouse.
     */
    const mousemoveDocument = function (e: MouseEvent) {
        mouseLocs.push({ x: e.pageX, y: e.pageY });

        if (mouseLocs.length > MOUSE_LOCS_TRACKED) {
            mouseLocs.shift();
        }
    };

    const resetMenu = function (e: Event) {
        if (e.target !== e.currentTarget) {
            return;
        }
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        setTimeout(function () {
            if (activeRow) {
                options.deactivate(activeRow);
            }

            activeRow = null;
        }, 100);
    };

    /**
     * Cancel possible row activations when leaving the menu entirely
     */
    const mouseleaveMenu = function (e: MouseEvent) {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // If exitMenu is supplied and returns true, deactivate the
        // currently active row on menu exit.
        if (options.exitMenu(e)) {
            if (activeRow) {
                options.deactivate(activeRow);
            }

            activeRow = null;
        }
    };

    /**
     * Trigger a possible row activation whenever entering a new row.
     */
    const mouseenterRow = function (e: MouseEvent) {
        const elm = e.currentTarget;
        if (timeoutId) {
            // Cancel any previous activation delays
            clearTimeout(timeoutId);
        }

        options.enter(elm);
        possiblyActivate(<HTMLElement>elm);
    };
    const mouseleaveRow = function (e: MouseEvent) {
        const elm = e.currentTarget;
        options.exit(<HTMLElement>elm);
    };

    /*
     * Immediately activate a row if the user clicks on it.
     */
    const clickRow = function (e: MouseEvent) {
        const elm = e.currentTarget;
        activate(<HTMLElement>elm, "click");
        options.click(elm);
    };

    /**
     * Activate a menu row.
     */
    const activate = function (row: HTMLElement, type?: string) {
        if (row === activeRow && !type) {
            return;
        }

        if (activeRow) {
            options.deactivate(activeRow);
        }

        options.activate(row, type);
        activeRow = row;
    };

    /**
     * Possibly activate a menu row. If mouse movement indicates that we
     * shouldn't activate yet because user may be trying to enter
     * a submenu's content, then delay and check again later.
     */
    const possiblyActivate = function (row: HTMLElement) {
        const delay = activationDelay();

        if (delay) {
            timeoutId = setTimeout(function () {
                possiblyActivate(row);
            }, delay);
        } else {
            activate(row);
        }
    };

    // offset helper
    const _offset = (elem: HTMLElement) => {
        const rect = elem.getBoundingClientRect();
        const win = elem?.ownerDocument?.defaultView || { pageYOffset: 0, pageXOffset: 0 };

        return {
            top: rect.top + win?.pageYOffset,
            left: rect.left + win?.pageXOffset,
        };
    };

    /**
     * Return the amount of time that should be used as a delay before the
     * currently hovered row is activated.
     *
     * Returns 0 if the activation should happen immediately. Otherwise,
     * returns the number of milliseconds that should be delayed before
     * checking again to see if the row should be activated.
     */
    const activationDelay = function () {
        let activeRowFound = false;

        if (activeRow) {
            // if active row is set
            // > check to see if it's of correct type
            const rows: NodeListOf<HTMLElement> | undefined = activeRow.parentElement?.querySelectorAll(
                options.submenuSelector,
            );
            if (rows) {
                activeRowFound = Array.prototype.some.call(rows, row => row === activeRow);
            }
            // for (let i = 0, maxI = rows.length; i < maxI; i++) {
            //     if (rows[i] === activeRow) {
            //         activeRowFound = true;
            //         break;
            //     }
            // }
        }

        if (!activeRowFound) {
            // if there is no other submenu row already active, then
            // go ahead and activate immediately.
            return 0;
        }

        const offset = _offset(menu);
        const upperLeft = {
            x: offset.left,
            y: offset.top - options.tolerance,
        };
        const upperRight = {
            x: offset.left + menu.offsetWidth,
            y: upperLeft.y,
        };
        const lowerLeft = {
            x: offset.left,
            y: offset.top + menu.offsetHeight + options.tolerance,
        };
        const lowerRight = {
            x: offset.left + menu.offsetWidth,
            y: lowerLeft.y,
        };
        const loc = mouseLocs[mouseLocs.length - 1];
        let prevLoc = mouseLocs[0];

        if (!loc) {
            return 0;
        }

        if (!prevLoc) {
            prevLoc = loc;
        }

        if (prevLoc.x < offset.left || prevLoc.x > lowerRight.x || prevLoc.y < offset.top || prevLoc.y > lowerRight.y) {
            // If the previous mouse location was outside of the entire
            // menu's bounds, immediately activate.
            return 0;
        }

        if (lastDelayLoc && loc.x === lastDelayLoc.x && loc.y === lastDelayLoc.y) {
            // If the mouse hasn't moved since the last time we checked
            // for activation status, immediately activate.
            return 0;
        }

        // Detect if the user is moving towards the currently activated
        // submenu.
        //
        // If the mouse is heading relatively clearly towards
        // the submenu's content, we should wait and give the user more
        // time before activating a new row. If the mouse is heading
        // elsewhere, we can immediately activate a new row.
        //
        // We detect this by calculating the slope formed between the
        // current mouse location and the upper/lower right points of
        // the menu. We do the same for the previous mouse location.
        // If the current mouse location's slopes are
        // increasing/decreasing appropriately compared to the
        // previous's, we know the user is moving toward the submenu.
        //
        // Note that since the y-axis increases as the cursor moves
        // down the screen, we are looking for the slope between the
        // cursor and the upper right corner to decrease over time, not
        // increase (somewhat counterintuitively).
        function slope(a: Coordinates, b: Coordinates) {
            return (b.y - a.y) / (b.x - a.x);
        }

        let decreasingCorner = upperRight;
        let increasingCorner = lowerRight;

        // Our expectations for decreasing or increasing slope values
        // depends on which direction the submenu opens relative to the
        // main menu. By default, if the menu opens on the right, we
        // expect the slope between the cursor and the upper right
        // corner to decrease over time, as explained above. If the
        // submenu opens in a different direction, we change our slope
        // expectations.
        if (options.submenuDirection === "left") {
            decreasingCorner = lowerLeft;
            increasingCorner = upperLeft;
        } else if (options.submenuDirection === "below") {
            decreasingCorner = lowerRight;
            increasingCorner = lowerLeft;
        } else if (options.submenuDirection === "above") {
            decreasingCorner = upperLeft;
            increasingCorner = upperRight;
        }

        const decreasingSlope = slope(loc, decreasingCorner);
        const increasingSlope = slope(loc, increasingCorner);
        const prevDecreasingSlope = slope(prevLoc, decreasingCorner);
        const prevIncreasingSlope = slope(prevLoc, increasingCorner);

        if (decreasingSlope < prevDecreasingSlope && increasingSlope > prevIncreasingSlope) {
            // Mouse is moving from previous location towards the
            // currently activated submenu. Delay before activating a
            // new menu row, because user may be moving into submenu.
            lastDelayLoc = loc;
            return DELAY;
        }

        lastDelayLoc = null;
        return 0;
    };

    /**
     * Hook up initial menu events
     */
    const hookUp = () => {
        menu?.addEventListener("reset", resetMenu, false);
        menu?.addEventListener("mouseleave", mouseleaveMenu, false);

        const li = menu.querySelectorAll(`:scope ${options.rowSelector}`);
        li.forEach(i => {
            i.addEventListener("mouseenter", (e: Event) => mouseenterRow(<MouseEvent>e), false);
            i.addEventListener("mouseleave", (e: Event) => mouseleaveRow(<MouseEvent>e), false);
            i.addEventListener("click", (e: Event) => clickRow(<MouseEvent>e), false);
        });
        document.addEventListener("mousemove", mousemoveDocument, false);
    };

    const unHook = () => {
        menu?.removeEventListener("reset", resetMenu);
        menu?.removeEventListener("mouseleave", mouseleaveMenu);

        const li = menu.querySelectorAll(`:scope ${options.rowSelector}`);
        li.forEach(i => {
            i.removeEventListener("mouseenter", (e: Event) => mouseenterRow(<MouseEvent>e));
            i.removeEventListener("mouseleave", (e: Event) => mouseleaveRow(<MouseEvent>e));
            i.removeEventListener("click", (e: Event) => clickRow(<MouseEvent>e));
        });
        document.removeEventListener("mousemove", mousemoveDocument);
    };

    hookUp();

    return {
        hookUp,
        unHook,
    };
}

export default menuAim;
